import React from 'react';

import {
   AGING_BUCKET_LABELS,
   AGING_COLORS,
   AGING_LABEL_INK,
} from '../constants';
import {
   formatCurrency,
   formatIsoDate,
   formatPercent,
   pluralTitulos,
} from '../utils/format';
import {
   AgingLegend,
   AgingSegment,
   AgingTrack,
   CardNote,
   CardSubtitle,
   CardTitle,
   LegendCount,
   LegendItem,
   LegendKey,
   LegendValue,
} from '../styles';
import { agingShape } from '../utils/shapes';

// ---------------------------------------------------------------------------
// Dobra 1 · Barra de aging da carteira em aberto.
//
// Part-to-whole de 5 fatias, larguras proporcionais ao VALOR. Os 4 buckets vêm
// sempre da API, mesmo zerados e em ordem estável — não há caso de ausência a
// tratar, só de largura zero (fatia some, legenda permanece).
//
// Separação entre fatias é ESPAÇO de 2px na cor da superfície, não borda —
// borda seria tinta que não é dado.
//
// O percentual só entra DENTRO da fatia quando cabe de fato. O piso de 9% não é
// estético: abaixo dele o texto encosta nas bordas ou é recortado, e rótulo
// cortado é pior que rótulo ausente. Quem ficou de fora não perde o número — a
// legenda abaixo traz valor e contagem de todas as cinco faixas, sempre.
// ---------------------------------------------------------------------------

const MIN_LABEL_SHARE = 9;

export default function AgingBar({ aging }) {
   const total = aging?.total_em_aberto?.valor ?? 0;

   // Ordem fixa: a vencer primeiro, depois a severidade crescente.
   const segments = [
      {
         key: 'a_vencer',
         label: 'A vencer',
         valor: aging?.a_vencer?.valor ?? 0,
         titulos: aging?.a_vencer?.titulos ?? 0,
      },
      ...(aging?.buckets ?? []).map((bucket) => ({
         key: bucket.bucket,
         label: AGING_BUCKET_LABELS[bucket.bucket] ?? bucket.bucket,
         valor: bucket.valor ?? 0,
         titulos: bucket.titulos ?? 0,
      })),
   ];

   // Sem carteira em aberto não há proporção a mostrar: a barra sairia como uma
   // faixa cinza sem significado. A legenda zerada conta a história inteira.
   const hasVolume = total > 0;

   const withShare = segments.map((segment) => ({
      ...segment,
      share: hasVolume ? (segment.valor / total) * 100 : 0,
   }));

   // O 90+ concentra a maior parte do valor vencido numa faixa que a escala
   // linear achata. Em vez de um card proprio (duplicaria a legenda), o peso
   // dele aparece como share DO VENCIDO no proprio card da legenda — que e a
   // comparacao que responde "onde esta o problema".
   const vencidoValue = aging?.vencido?.valor ?? 0;
   const shareOfOverdue = (segment) =>
      segment.key !== 'a_vencer' && vencidoValue > 0 && segment.valor > 0
         ? (segment.valor / vencidoValue) * 100
         : null;

   return (
      <>
         <div>
            <CardTitle as="h3">Aging da carteira em aberto</CardTitle>
            <CardSubtitle>
               Posição em {formatIsoDate(aging?.as_of)} ·{' '}
               {pluralTitulos(aging?.total_em_aberto?.titulos ?? 0)} ·{' '}
               {formatCurrency(total)} em aberto
            </CardSubtitle>
         </div>

         {hasVolume ? (
            <AgingTrack
               role="img"
               aria-label={`Distribuição da carteira em aberto: ${withShare
                  .map(
                     (segment) =>
                        `${segment.label}, ${formatPercent(segment.share, 1)}`,
                  )
                  .join('; ')}`}
            >
               {withShare
                  .filter((segment) => segment.share > 0)
                  .map((segment) => (
                     <AgingSegment
                        key={segment.key}
                        style={{ flexGrow: segment.share, flexBasis: 0 }}
                        $color={AGING_COLORS[segment.key]}
                        $ink={AGING_LABEL_INK[segment.key]}
                        title={`${segment.label} · ${formatCurrency(segment.valor)} · ${pluralTitulos(segment.titulos)}`}
                     >
                        {segment.share >= MIN_LABEL_SHARE
                           ? formatPercent(segment.share, 0)
                           : ''}
                     </AgingSegment>
                  ))}
            </AgingTrack>
         ) : null}

         <AgingLegend>
            {withShare.map((segment) => {
               const overdueShare = shareOfOverdue(segment);
               return (
                  <LegendItem key={segment.key}>
                     <LegendKey $color={AGING_COLORS[segment.key]}>
                        {segment.label}
                     </LegendKey>
                     <LegendValue>
                        {segment.titulos ? formatCurrency(segment.valor) : '—'}
                     </LegendValue>
                     <LegendCount>
                        {pluralTitulos(segment.titulos)}
                        {overdueShare !== null
                           ? ` · ${formatPercent(overdueShare, 0)} do vencido`
                           : ''}
                     </LegendCount>
                  </LegendItem>
               );
            })}
         </AgingLegend>

         <CardNote>Vencimento no próprio dia conta como a vencer.</CardNote>
      </>
   );
}

AgingBar.propTypes = {
   aging: agingShape.isRequired,
};
